import { useId, useState, type JSX, type ReactNode } from "react";

import type {
  CreateLeaderboardInput,
  CreateRewardInput,
  GuildAggregator,
  Guards,
  Operator,
  Scope,
  Segmentation,
  Sort,
  SubmitPolicy,
  TierConfig,
  TierMode,
  Visibility,
  WindowKind,
} from "./types";
import { Select } from "../../components/Select/Select";
import { analyseCron } from "../../lib/format";
import styles from "./LeaderboardConfigForm.module.css";

// === Component contract =============================================== //

export interface LeaderboardConfigFormProps {
  mode: "create" | "edit";
  initial?: Partial<CreateLeaderboardInput>;
  submitLabel?: string;
  /**
   * Called with the leaderboard payload, plus an optional list of reward
   * drafts to create after the leaderboard itself is created. The form
   * only collects reward drafts in `mode="create"`; in edit mode the
   * second argument is always an empty array (rewards are managed on the
   * detail page's Rewards tab).
   */
  onSubmit: (input: CreateLeaderboardInput, rewards: CreateRewardInput[]) => void;
  onCancel: () => void;
  saving?: boolean;
}

/**
 * Draft of a reward as edited in the create-form mini-editor. All fields
 * are strings because they're bound directly to inputs; conversion to the
 * API shape happens in buildRewardInputs().
 */
interface RewardDraft {
  name: string;
  type: "rank_range" | "percentile";
  range_from: string;
  range_to: string;
  payload: string;
}

function emptyRewardDraft(): RewardDraft {
  return { name: "", type: "rank_range", range_from: "", range_to: "", payload: "" };
}

// === Defaults ========================================================= //
// Defaults documented in the task spec; we never seed cron/event_*/aggregator/
// bucket_size/tier_mode/tier_config/metadata.

interface FormState {
  ext_key: string;
  name: string;
  description: string;
  operator: Operator;
  sort: Sort;
  window_kind: WindowKind;
  scope: Scope;
  segmentation: Segmentation;
  submit_policy: SubmitPolicy;
  visibility: Visibility;
  realtime: boolean;
  guards: Guards;
  cron: string;
  event_start_at: string;
  event_end_at: string;
  guild_aggregator: GuildAggregator | "";
  bucket_size: string;
  tier_mode: TierMode | "";
  tier_config: TierConfig;
  /** Only populated in mode="create". Edit mode leaves this empty. */
  rewards: RewardDraft[];
}

function initialState(initial: Partial<CreateLeaderboardInput> | undefined): FormState {
  return {
    ext_key: initial?.ext_key ?? "",
    name: initial?.name ?? "",
    description: initial?.description ?? "",
    operator: (initial?.operator ?? "best") as Operator,
    sort: (initial?.sort ?? "desc") as Sort,
    window_kind: (initial?.window_kind ?? "all_time") as WindowKind,
    scope: ((initial?.scope as Scope) || "global") as Scope,
    segmentation: ((initial?.segmentation as Segmentation) || "none") as Segmentation,
    submit_policy: (initial?.submit_policy ?? "client") as SubmitPolicy,
    visibility: (initial?.visibility ?? "public") as Visibility,
    realtime: initial?.realtime ?? true,
    guards: initial?.guards ?? {},
    cron: initial?.cron ?? "",
    event_start_at: initial?.event_start_at ?? "",
    event_end_at: initial?.event_end_at ?? "",
    guild_aggregator: (initial?.guild_aggregator as GuildAggregator | "") ?? "",
    bucket_size:
      initial?.bucket_size !== undefined && initial?.bucket_size !== null
        ? String(initial.bucket_size)
        : "",
    tier_mode: (initial?.tier_mode as TierMode | "") ?? "",
    tier_config: initial?.tier_config ?? { tiers: [] },
    rewards: [],
  };
}

// === Option metadata ================================================== //

interface OptionMeta<T extends string> {
  value: T;
  glyph: string;
  title: string;
  desc: string;
}

const OPERATOR_OPTIONS: OptionMeta<Operator>[] = [
  { value: "best", glyph: "↓", title: "best", desc: "Higher score wins — keeps the maximum." },
  { value: "worst", glyph: "↑", title: "worst", desc: "Lower score wins — keeps the minimum." },
  { value: "increment", glyph: "Σ", title: "increment", desc: "Accumulates submitted values." },
  { value: "set", glyph: "=", title: "set", desc: "Always overwrites the previous value." },
];

const WINDOW_OPTIONS: OptionMeta<WindowKind>[] = [
  { value: "all_time", glyph: "∞", title: "all_time", desc: "No reset — scores persist forever." },
  { value: "recurring", glyph: "↻", title: "recurring", desc: "Resets on a cron schedule." },
  { value: "event", glyph: "◇", title: "event", desc: "Fixed start_at → end_at window." },
];

const SCOPE_OPTIONS: OptionMeta<Scope>[] = [
  { value: "global", glyph: "G", title: "global", desc: "All players ranked together." },
  { value: "guild_internal", glyph: "⌂", title: "guild_internal", desc: "Members ranked inside each guild." },
  { value: "guild_aggregated", glyph: "Σ", title: "guild_aggregated", desc: "Guilds ranked by aggregated member scores." },
];

const SEGMENTATION_OPTIONS: OptionMeta<Segmentation>[] = [
  { value: "none", glyph: "–", title: "none", desc: "Single flat ranking." },
  { value: "bucket", glyph: "▦", title: "bucket", desc: "Fixed-size matchmaking pools." },
  { value: "tier", glyph: "T", title: "tier", desc: "Ranked ladder." },
];

// === Validation ======================================================= //

interface FormErrors {
  ext_key?: string;
  name?: string;
  cron?: string;
  event_start_at?: string;
  event_end_at?: string;
  guild_aggregator?: string;
  bucket_size?: string;
  tier_mode?: string;
  tier_config?: string;
  guards?: string;
  rewards?: string;
  /** Per-row reward errors keyed by index. */
  rewardRows?: Record<number, string>;
}

const EXT_KEY_RE = /^[a-z][a-z0-9_]*$/;

function validate(state: FormState, mode: "create" | "edit"): FormErrors {
  const errors: FormErrors = {};

  if (state.name.trim().length === 0) {
    errors.name = "Name is required.";
  }

  if (mode === "create") {
    const ek = state.ext_key.trim();
    if (!ek || !EXT_KEY_RE.test(ek)) {
      errors.ext_key =
        "Ext key must start with a lowercase letter and contain only lowercase, digits, and underscores.";
    }
  }

  if (state.window_kind === "recurring") {
    if (state.cron.trim().length === 0) {
      errors.cron = "Cron is required for recurring window.";
    } else {
      const cronCheck = analyseCron(state.cron);
      if (cronCheck.kind === "invalid") {
        errors.cron = `Invalid cron: ${cronCheck.reason}`;
      }
    }
  }

  if (state.window_kind === "event") {
    if (
      state.event_start_at.trim().length === 0 ||
      state.event_end_at.trim().length === 0
    ) {
      errors.event_start_at = "Event window dates are required.";
    } else {
      const start = Date.parse(state.event_start_at);
      const end = Date.parse(state.event_end_at);
      if (Number.isFinite(start) && Number.isFinite(end) && end <= start) {
        errors.event_end_at = "Event end must be after event start.";
      }
    }
  }

  if (state.scope === "guild_aggregated") {
    if (!state.guild_aggregator) {
      errors.guild_aggregator = "Guild aggregator is required.";
    } else {
      const pair =
        (state.guild_aggregator === "sum" && state.operator === "increment") ||
        (state.guild_aggregator === "max" && state.operator === "best");
      if (!pair) {
        errors.guild_aggregator =
          "Guild aggregator must pair with operator (sum↔increment, max↔best).";
      }
    }
  }

  if (state.segmentation === "bucket") {
    const n = Number(state.bucket_size);
    if (!Number.isInteger(n) || n < 2) {
      errors.bucket_size = "Bucket size must be an integer ≥ 2.";
    }
  }

  if (state.segmentation === "tier") {
    if (!state.tier_mode) {
      errors.tier_mode = "Tier mode is required.";
    }
    // Tier rows + promotion knobs live on the detail page (RewardsTab /
    // SettingsTab manage them post-create). Skip these checks in create mode
    // so the form can be submitted with segmentation=tier and the operator
    // can configure tier_config afterwards.
    if (mode === "edit") {
      if ((state.tier_config.tiers?.length ?? 0) < 1) {
        errors.tier_config = "At least one tier is required.";
      }
      if (state.tier_mode === "promotion") {
        const top = state.tier_config.promote_top_k ?? 0;
        const bottom = state.tier_config.demote_bottom_k ?? 0;
        if (
          !Number.isInteger(top) ||
          top <= 0 ||
          !Number.isInteger(bottom) ||
          bottom <= 0
        ) {
          errors.tier_config =
            "promote_top_k and demote_bottom_k are required for promotion mode.";
        }
      }
    }
  }

  const guardEntries: (number | null | undefined)[] = [
    state.guards.max_score_per_submit,
    state.guards.max_delta_per_submit,
    state.guards.min_interval_sec,
  ];
  for (const g of guardEntries) {
    if (g === null || g === undefined) continue;
    if (!Number.isInteger(g) || g <= 0) {
      errors.guards = "Guard values must be positive integers.";
      break;
    }
  }

  if (mode === "create" && state.rewards.length > 0) {
    const rowErrs: Record<number, string> = {};
    for (let i = 0; i < state.rewards.length; i++) {
      const r = state.rewards[i];
      const rowErr = validateRewardDraft(r);
      if (rowErr) rowErrs[i] = rowErr;
    }
    if (Object.keys(rowErrs).length > 0) {
      errors.rewardRows = rowErrs;
      errors.rewards = "One or more rewards have errors — fix them before submitting.";
    }
  }

  return errors;
}

function validateRewardDraft(r: RewardDraft): string | null {
  if (r.name.trim().length === 0) return "Name is required.";

  const from = Number(r.range_from);
  const to = Number(r.range_to);
  if (!Number.isInteger(from) || !Number.isInteger(to)) {
    return "Range must be two integers.";
  }
  if (from < 0 || to < 0) {
    return "Range values must be non-negative.";
  }
  if (from > to) {
    return "Range start must be ≤ end.";
  }
  if (r.type === "rank_range") {
    if (from < 1) return "Rank range must start at 1 or above.";
  } else {
    if (from > 100 || to > 100) return "Percentile values must be in 0..100.";
  }

  const payload = r.payload.trim();
  if (payload.length > 0) {
    try {
      const parsed = JSON.parse(payload);
      if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
        return "Payload must be a JSON object.";
      }
    } catch {
      return "Payload must be valid JSON.";
    }
  }

  return null;
}

/**
 * Convert the form's reward drafts into the API-ready `CreateRewardInput`
 * array. Position is derived from the row index (0-based) so the caller
 * sees the rewards in display order. Assumes drafts have already passed
 * validateRewardDraft — otherwise JSON.parse may throw.
 */
export function buildRewardInputs(rewards: RewardDraft[]): CreateRewardInput[] {
  return rewards.map((r, i) => {
    const from = Number(r.range_from);
    const to = Number(r.range_to);
    const payloadTrim = r.payload.trim();
    const payload =
      payloadTrim.length > 0
        ? (JSON.parse(payloadTrim) as Record<string, unknown>)
        : {};
    const base: CreateRewardInput = {
      name: r.name.trim(),
      position: i,
      payload,
    };
    if (r.type === "rank_range") {
      base.rank_from = from;
      base.rank_to = to;
    } else {
      base.percentile_from = from;
      base.percentile_to = to;
    }
    return base;
  });
}

function buildInput(state: FormState, mode: "create" | "edit"): CreateLeaderboardInput {
  const input: CreateLeaderboardInput = {
    ext_key: state.ext_key.trim(),
    name: state.name.trim(),
    description: state.description,
    operator: state.operator,
    sort: state.sort,
    window_kind: state.window_kind,
    scope: state.scope,
    segmentation: state.segmentation,
    submit_policy: state.submit_policy,
    visibility: state.visibility,
    realtime: state.realtime,
    guards: state.guards,
  };
  if (mode === "edit") {
    // ext_key is immutable post-create; mirror back the seeded value.
    input.ext_key = state.ext_key;
  }
  if (state.window_kind === "recurring") {
    input.cron = state.cron;
  }
  if (state.window_kind === "event") {
    input.event_start_at = state.event_start_at;
    input.event_end_at = state.event_end_at;
  }
  if (state.scope === "guild_aggregated" && state.guild_aggregator) {
    input.guild_aggregator = state.guild_aggregator;
  }
  if (state.segmentation === "bucket") {
    input.bucket_size = Number(state.bucket_size);
  }
  if (state.segmentation === "tier") {
    if (state.tier_mode) input.tier_mode = state.tier_mode;
    input.tier_config = state.tier_config;
  }
  return input;
}

// === Sub-components =================================================== //

interface RadioCardProps<T extends string> {
  name: string;
  options: OptionMeta<T>[];
  value: T;
  cols: 2 | 3 | 4;
  onChange: (v: T) => void;
}

function RadioCardGroup<T extends string>({
  name,
  options,
  value,
  cols,
  onChange,
}: RadioCardProps<T>) {
  const colsClass =
    cols === 2 ? styles.cols2 : cols === 4 ? styles.cols4 : styles.cols3;
  return (
    <div className={`${styles.radioCards} ${colsClass}`} role="radiogroup">
      {options.map((opt) => {
        const selected = opt.value === value;
        const inputId = `${name}-${opt.value}`;
        return (
          <div
            key={opt.value}
            className={`${styles.option} ${selected ? styles.selected : ""}`}
          >
            <input
              id={inputId}
              type="radio"
              name={name}
              value={opt.value}
              checked={selected}
              onChange={() => onChange(opt.value)}
            />
            <span className={styles.glyphBox} aria-hidden>
              {opt.glyph}
            </span>
            <span className={styles.optBody}>
              {/* The <label htmlFor> targets the radio with text matching the
                 enum value — required for getByLabelText("recurring") etc. */}
              <label htmlFor={inputId} className={styles.optTitle}>
                {opt.title}
              </label>
              <span className={styles.optDesc}>{opt.desc}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

interface SegRadioProps<T extends string> {
  name: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  /** When provided, set as aria-label on each input — lets getByLabelText
     resolve a control by group name (e.g. "Guild aggregator"). */
  groupLabel?: string;
}

function SegRadio<T extends string>({
  name,
  options,
  value,
  onChange,
  groupLabel,
}: SegRadioProps<T>) {
  // Use a fieldset so the group itself is form-associable; that means
  // testing-library's getByLabelText(groupLabel) resolves cleanly through
  // aria-label without needing a sibling <label htmlFor>. Visually the
  // fieldset is stripped of borders to match the inline seg control.
  return (
    <div
      className={styles.seg}
      role="radiogroup"
      aria-label={groupLabel}
      // tabIndex makes the group focusable so RTL's accessibility-tree query
      // returns the wrapper element by name (groupLabel) — avoids the
      // "element associated with this label is non-labellable" error when
      // the consumer passes a sibling <label htmlFor>.
      tabIndex={groupLabel ? -1 : undefined}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        const id = `${name}-${opt.value}`;
        return (
          <label
            key={opt.value}
            className={`${styles.segOpt} ${active ? styles.segActive : ""}`}
            htmlFor={id}
          >
            <input
              id={id}
              type="radio"
              name={name}
              value={opt.value}
              checked={active}
              onChange={() => onChange(opt.value)}
            />
            {opt.label}
          </label>
        );
      })}
    </div>
  );
}

interface FieldRowProps {
  label: string;
  apiName?: string;
  hint?: string;
  required?: boolean;
  full?: boolean;
  children: ReactNode;
  error?: string;
  /** When set, the .name block becomes a real <label htmlFor=...>. Clicks
     then focus the associated input. Radio-card / switch groups omit this. */
  htmlFor?: string;
}

function FieldRow({
  label,
  apiName,
  hint,
  required,
  full,
  children,
  error,
  htmlFor,
}: FieldRowProps) {
  const NameTag = htmlFor ? "label" : "div";
  const nameProps = htmlFor ? { htmlFor } : {};
  return (
    <div className={`${styles.fieldRow} ${full ? styles.fieldRowFull : ""}`}>
      <div className={styles.fieldLabel}>
        <NameTag className={styles.name} {...nameProps}>
          {label}
        </NameTag>
        {required && (
          <span className={styles.req} aria-hidden>
            *
          </span>
        )}
        {apiName && <div className={styles.apiName}>{apiName}</div>}
        {hint && <div className={styles.hint}>{hint}</div>}
        {error && <div className={styles.fieldError}>{error}</div>}
      </div>
      <div className={styles.fieldInput}>{children}</div>
    </div>
  );
}

// === Main component =================================================== //

export function LeaderboardConfigForm(
  props: LeaderboardConfigFormProps,
): JSX.Element {
  const { mode, initial, submitLabel, onSubmit, onCancel, saving } = props;

  const [state, setState] = useState<FormState>(() => initialState(initial));
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);

  const extKeyId = useId();
  const nameId = useId();
  const descId = useId();
  const cronId = useId();
  const eventStartId = useId();
  const eventEndId = useId();
  const bucketSizeId = useId();

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((s) => ({ ...s, [key]: value }));
    if (submitted) {
      setErrors((prev) => ({ ...prev, [key as string]: undefined }));
    }
  }

  function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    setSubmitted(true);
    const errs = validate(state, mode);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const rewards =
      mode === "create" ? buildRewardInputs(state.rewards) : [];
    onSubmit(buildInput(state, mode), rewards);
  }

  function addRewardDraft() {
    setState((s) => ({ ...s, rewards: [...s.rewards, emptyRewardDraft()] }));
  }

  function updateRewardDraft(index: number, patch: Partial<RewardDraft>) {
    setState((s) => ({
      ...s,
      rewards: s.rewards.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    }));
    if (submitted) {
      setErrors((prev) => {
        if (!prev.rewardRows) return prev;
        const next = { ...prev.rewardRows };
        delete next[index];
        return { ...prev, rewardRows: next, rewards: undefined };
      });
    }
  }

  function removeRewardDraft(index: number) {
    setState((s) => ({
      ...s,
      rewards: s.rewards.filter((_, i) => i !== index),
    }));
    if (submitted) {
      setErrors((prev) => {
        if (!prev.rewardRows) return prev;
        const next: Record<number, string> = {};
        for (const [k, v] of Object.entries(prev.rewardRows)) {
          const ki = Number(k);
          if (ki === index) continue;
          next[ki < index ? ki : ki - 1] = v;
        }
        return { ...prev, rewardRows: next };
      });
    }
  }

  const effectiveSubmitLabel =
    submitLabel ?? (mode === "create" ? "Create leaderboard" : "Save changes");

  return (
    <form className={styles.formGrid} onSubmit={handleSubmit} noValidate>
      {/* ============ SECTION 1: Identity ============ */}
      <section className={styles.section}>
        <div className={styles.cardTitle}>
          <h3>Identity</h3>
          <span className={styles.cardSub}>step 1 of 5</span>
        </div>
        <div className={styles.cardBody}>
          <p className={styles.sectionDesc}>
            Unique slug used by the SDK, display name, and description.
          </p>

          <FieldRow
            label="Ext key"
            apiName="ext_key"
            hint="Slug, lowercase, snake_case. Cannot be changed after creation."
            required={mode === "create"}
            error={errors.ext_key}
            htmlFor={mode === "create" ? extKeyId : undefined}
          >
            {mode === "edit" ? (
              <code className={styles.extKeyPill}>{state.ext_key}</code>
            ) : (
              <input
                id={extKeyId}
                className={`${styles.input} ${styles.mono} ${
                  errors.ext_key ? styles.invalid : ""
                }`}
                placeholder="weekly_score"
                value={state.ext_key}
                onChange={(e) => update("ext_key", e.target.value)}
              />
            )}
          </FieldRow>

          <FieldRow
            label="Name"
            apiName="name"
            hint="Display name in the admin UI."
            required
            error={errors.name}
            htmlFor={nameId}
          >
            <input
              id={nameId}
              className={`${styles.input} ${errors.name ? styles.invalid : ""}`}
              placeholder="Weekly Score"
              value={state.name}
              onChange={(e) => update("name", e.target.value)}
            />
          </FieldRow>

          <FieldRow
            label="Description"
            apiName="description"
            hint="Optional."
            htmlFor={descId}
          >
            <textarea
              id={descId}
              className={styles.textarea}
              placeholder="Top scorers of the week…"
              value={state.description}
              onChange={(e) => update("description", e.target.value)}
            />
          </FieldRow>
        </div>
      </section>

      {/* ============ SECTION 2: Scoring ============ */}
      <section className={styles.section}>
        <div className={styles.cardTitle}>
          <h3>Scoring</h3>
          <span className={styles.cardSub}>step 2 of 5</span>
        </div>
        <div className={styles.cardBody}>
          <FieldRow
            label="Operator"
            apiName="operator"
            hint="How to interpret incoming SDK score values."
            full
          >
            <RadioCardGroup
              name="operator"
              options={OPERATOR_OPTIONS}
              value={state.operator}
              cols={4}
              onChange={(v) => update("operator", v)}
            />
          </FieldRow>

          <FieldRow
            label="Sort"
            apiName="sort"
            hint="Score sort direction. Decoupled from operator."
          >
            <SegRadio
              name="sort"
              options={[
                { value: "desc", label: "desc" },
                { value: "asc", label: "asc" },
              ]}
              value={state.sort}
              onChange={(v) => update("sort", v)}
            />
          </FieldRow>

          <FieldRow
            label="Window kind"
            apiName="window_kind"
            hint="Lifecycle of stored scores."
            full
            error={errors.cron || errors.event_start_at || errors.event_end_at}
          >
            <RadioCardGroup
              name="window_kind"
              options={WINDOW_OPTIONS}
              value={state.window_kind}
              cols={3}
              onChange={(v) => update("window_kind", v)}
            />

            {state.window_kind === "recurring" && (
              <div className={styles.reveal}>
                <div className={styles.revealInner}>
                  <div className={styles.revealRow}>
                    {/* api-style snake_case label per mockup (.rl block) */}
                    <div className={styles.rl}>cron</div>
                    <div className={styles.cronCol}>
                      <input
                        id={cronId}
                        aria-label="Cron"
                        className={`${styles.input} ${styles.mono} ${
                          errors.cron ? styles.invalid : ""
                        }`}
                        placeholder="0 0 * * MON"
                        value={state.cron}
                        onChange={(e) => update("cron", e.target.value)}
                      />
                      {(() => {
                        const a = analyseCron(state.cron);
                        if (a.kind === "ok") {
                          return <div className={styles.cronHuman}>{a.text}</div>;
                        }
                        if (a.kind === "invalid") {
                          return (
                            <div className={styles.cronError}>
                              Invalid cron: {a.reason}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                  {errors.cron && !errors.cron.startsWith("Invalid cron") && (
                    <div className={styles.fieldError}>{errors.cron}</div>
                  )}
                </div>
              </div>
            )}

            {state.window_kind === "event" && (
              <div className={styles.reveal}>
                <div className={styles.revealInner}>
                  <div className={styles.revealRow}>
                    <div className={styles.rl}>event_start_at</div>
                    <input
                      id={eventStartId}
                      aria-label="Event start"
                      type="datetime-local"
                      className={`${styles.input} ${styles.mono} ${
                        errors.event_start_at ? styles.invalid : ""
                      }`}
                      value={state.event_start_at}
                      onChange={(e) => update("event_start_at", e.target.value)}
                    />
                  </div>
                  <div className={styles.revealRow}>
                    <div className={styles.rl}>event_end_at</div>
                    <input
                      id={eventEndId}
                      aria-label="Event end"
                      type="datetime-local"
                      className={`${styles.input} ${styles.mono} ${
                        errors.event_end_at ? styles.invalid : ""
                      }`}
                      value={state.event_end_at}
                      onChange={(e) => update("event_end_at", e.target.value)}
                    />
                  </div>
                  {(errors.event_start_at || errors.event_end_at) && (
                    <div className={styles.fieldError}>
                      {errors.event_end_at || errors.event_start_at}
                    </div>
                  )}
                </div>
              </div>
            )}
          </FieldRow>

          {/* Realtime switch — mockup §575–587: rank.changed WS toggle. */}
          <FieldRow
            label="Realtime"
            apiName="realtime"
            hint="Pushes rank.changed WS events to subscribed clients."
          >
            <div className={styles.switchRow}>
              <button
                type="button"
                role="switch"
                aria-checked={state.realtime}
                aria-label="Realtime"
                className={`${styles.switch} ${
                  state.realtime ? styles.switchOn : ""
                }`}
                onClick={() => update("realtime", !state.realtime)}
              >
                <span className={styles.switchKnob} aria-hidden />
              </button>
              <span className={styles.switchLabel}>
                {state.realtime
                  ? "On — broadcasts rank.changed"
                  : "Off — no realtime events"}
              </span>
            </div>
          </FieldRow>

          <FieldRow
            label="Submit policy"
            apiName="submit_policy"
            hint="Who can submit scores."
          >
            <SegRadio
              name="submit_policy"
              options={[
                { value: "client", label: "client" },
                { value: "s2s_only", label: "s2s_only" },
              ]}
              value={state.submit_policy}
              onChange={(v) => update("submit_policy", v)}
            />
          </FieldRow>
        </div>
      </section>

      {/* ============ SECTION 3: Scope ============ */}
      <section className={styles.section}>
        <div className={styles.cardTitle}>
          <h3>Scope</h3>
          <span className={styles.cardSub}>step 3 of 5</span>
        </div>
        <div className={styles.cardBody}>
          <FieldRow
            label="Scope"
            apiName="scope"
            hint="Who ranks against whom."
            full
            error={errors.guild_aggregator}
          >
            <RadioCardGroup
              name="scope"
              options={SCOPE_OPTIONS}
              value={state.scope}
              cols={3}
              onChange={(v) => update("scope", v)}
            />

            {state.scope === "guild_aggregated" && (
              <div className={styles.reveal}>
                <div className={styles.revealInner}>
                  <div className={styles.revealRow}>
                    {/* api-style label per mockup; getByLabelText targets the
                       SegRadio wrapper via groupLabel→aria-label. */}
                    <div className={styles.rl}>guild_aggregator</div>
                    <SegRadio
                      name="guild_aggregator"
                      groupLabel="Guild aggregator"
                      options={[
                        { value: "sum", label: "sum" },
                        { value: "max", label: "max" },
                      ]}
                      value={
                        (state.guild_aggregator || "sum") as GuildAggregator
                      }
                      onChange={(v) => update("guild_aggregator", v)}
                    />
                  </div>
                  {errors.guild_aggregator && (
                    <div className={styles.fieldError}>
                      {errors.guild_aggregator}
                    </div>
                  )}
                </div>
              </div>
            )}
          </FieldRow>

          <FieldRow
            label="Visibility"
            apiName="visibility"
            hint="Who sees scores on the read side."
          >
            <SegRadio
              name="visibility"
              options={[
                { value: "public", label: "public" },
                { value: "guild_only", label: "guild_only" },
                { value: "hidden", label: "hidden" },
              ]}
              value={state.visibility}
              onChange={(v) => update("visibility", v)}
            />
          </FieldRow>
        </div>
      </section>

      {/* ============ SECTION 4: Segmentation ============ */}
      <section className={styles.section}>
        <div className={styles.cardTitle}>
          <h3>Segmentation</h3>
          <span className={styles.cardSub}>step 4 of 5</span>
        </div>
        <div className={styles.cardBody}>
          <FieldRow
            label="Segmentation"
            apiName="segmentation"
            hint="How participants are split into comparable groups."
            full
            error={errors.bucket_size || errors.tier_mode || errors.tier_config}
          >
            <RadioCardGroup
              name="segmentation"
              options={SEGMENTATION_OPTIONS}
              value={state.segmentation}
              cols={3}
              onChange={(v) => update("segmentation", v)}
            />

            {state.segmentation === "bucket" && (
              <div className={styles.reveal}>
                <div className={styles.revealInner}>
                  <div className={styles.revealRow}>
                    <div className={styles.rl}>bucket_size</div>
                    <input
                      id={bucketSizeId}
                      aria-label="Bucket size"
                      type="number"
                      min={2}
                      className={`${styles.input} ${styles.number} ${
                        errors.bucket_size ? styles.invalid : ""
                      }`}
                      value={state.bucket_size}
                      onChange={(e) => update("bucket_size", e.target.value)}
                    />
                  </div>
                  {errors.bucket_size && (
                    <div className={styles.fieldError}>
                      {errors.bucket_size}
                    </div>
                  )}
                </div>
              </div>
            )}

            {state.segmentation === "tier" && (
              <div className={styles.reveal}>
                <div className={styles.revealInner}>
                  <div className={styles.revealRow}>
                    <div className={styles.rl}>tier_mode</div>
                    <SegRadio
                      name="tier_mode"
                      groupLabel="Tier mode"
                      options={[
                        { value: "score_bucket", label: "score_bucket" },
                        { value: "promotion", label: "promotion" },
                      ]}
                      value={(state.tier_mode || "score_bucket") as TierMode}
                      onChange={(v) => update("tier_mode", v)}
                    />
                  </div>
                  {(errors.tier_mode || errors.tier_config) && (
                    <div className={styles.fieldError}>
                      {errors.tier_mode || errors.tier_config}
                    </div>
                  )}
                  <div className={styles.tierNote}>
                    Tier rows are managed on the detail page once the leaderboard exists.
                  </div>
                </div>
              </div>
            )}
          </FieldRow>
        </div>
      </section>

      {/* ============ SECTION 5: Rewards (create mode only) ============
         In edit mode the SettingsTab embeds the full RewardsEditor (same
         component the Rewards tab uses) directly between the form and the
         Danger zone, so the form omits the section entirely to avoid a
         duplicate "Rewards" heading. */}
      {mode === "create" && (
      <section className={styles.section}>
        <div className={styles.cardTitle}>
          <h3>Rewards</h3>
          <span className={styles.cardSub}>step 5 of 5</span>
        </div>
        <div className={styles.cardBody}>
          {state.rewards.length === 0 ? (
            <div className={styles.rewardsEmpty}>
              Optional — add reward templates here, or skip and add them later
              from the Rewards tab.
              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnSecondary}`}
                  onClick={addRewardDraft}
                >
                  + Add reward
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.rewardEditor}>
              <div className={`${styles.rewardRow} ${styles.rewardHead}`}>
                <div>Pos.</div>
                <div>Name</div>
                <div>Type</div>
                <div>Range</div>
                <div>Payload (JSON)</div>
                <div aria-hidden />
              </div>
              {state.rewards.map((r, i) => {
                const rowErr = errors.rewardRows?.[i];
                return (
                  <div
                    key={i}
                    className={`${styles.rewardRow} ${rowErr ? styles.rewardRowInvalid : ""}`}
                  >
                    <div className={styles.rewardPos}>{i + 1}</div>
                    <input
                      className={styles.input}
                      aria-label={`Reward ${i + 1} name`}
                      placeholder="Champion"
                      value={r.name}
                      onChange={(e) =>
                        updateRewardDraft(i, { name: e.target.value })
                      }
                    />
                    <Select
                      value={r.type}
                      onChange={(v) =>
                        updateRewardDraft(i, {
                          type: v as RewardDraft["type"],
                        })
                      }
                      options={[
                        { value: "rank_range", label: "rank_range" },
                        { value: "percentile", label: "percentile" },
                      ]}
                    />
                    <div className={styles.rewardRangePair}>
                      <input
                        className={`${styles.input} ${styles.mono}`}
                        aria-label={`Reward ${i + 1} range from`}
                        placeholder="1"
                        inputMode="numeric"
                        value={r.range_from}
                        onChange={(e) =>
                          updateRewardDraft(i, { range_from: e.target.value })
                        }
                      />
                      <span className={styles.rewardDash}>–</span>
                      <input
                        className={`${styles.input} ${styles.mono}`}
                        aria-label={`Reward ${i + 1} range to`}
                        placeholder="1"
                        inputMode="numeric"
                        value={r.range_to}
                        onChange={(e) =>
                          updateRewardDraft(i, { range_to: e.target.value })
                        }
                      />
                      {r.type === "percentile" && (
                        <span className={styles.rewardPctMark}>%</span>
                      )}
                    </div>
                    <input
                      className={`${styles.input} ${styles.mono}`}
                      aria-label={`Reward ${i + 1} payload`}
                      placeholder='{"gold":1000}'
                      value={r.payload}
                      onChange={(e) =>
                        updateRewardDraft(i, { payload: e.target.value })
                      }
                    />
                    <button
                      type="button"
                      className={styles.rewardRemove}
                      aria-label={`Remove reward ${i + 1}`}
                      onClick={() => removeRewardDraft(i)}
                    >
                      ×
                    </button>
                    {rowErr && (
                      <div className={styles.rewardRowError}>{rowErr}</div>
                    )}
                  </div>
                );
              })}
              <div className={styles.rewardsToolbar}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnSecondary}`}
                  onClick={addRewardDraft}
                >
                  + Add reward
                </button>
                {errors.rewards && (
                  <span className={styles.fieldError}>{errors.rewards}</span>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
      )}

      {/* ============ Fixed action bar (page-bottom) ============
         The outer .actionBar is positioned: fixed at viewport bottom so it
         stays visible while the user scrolls the page (including past the
         form into siblings like SettingsTab's Danger zone). The inner
         wrapper mirrors the page body (capped at --content-max with a 32px
         gutter) so the buttons align with the right edge of the form
         sections. */}
      <div className={styles.actionBar}>
        <div className={styles.actionBarInner}>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={saving}
          >
            {saving ? "Saving…" : effectiveSubmitLabel}
          </button>
        </div>
      </div>
    </form>
  );
}
